import React, { useState } from 'react'
import { 
  Save, 
  Globe, 
  Shield, 
  Database, 
  Mail, 
  Palette,
  Code,
  Eye,
  EyeOff
} from 'lucide-react'
import toast from 'react-hot-toast'

interface SettingsConfig {
  general: {
    app_name: string
    app_url: string
    timezone: string
    date_format: string
    language: string
  }
  security: {
    password_min_length: number
    require_2fa: boolean
    session_timeout: number
    max_login_attempts: number
  }
  database: {
    host: string
    port: number
    name: string
    username: string
    password: string
    ssl_enabled: boolean
  }
  email: {
    driver: string
    smtp_host: string
    smtp_port: number
    smtp_username: string
    smtp_password: string
    from_address: string
    from_name: string
  }
  api: {
    rate_limit: number
    cors_enabled: boolean
    allowed_origins: string[]
    api_version: string
  }
  ui: {
    theme: string
    primary_color: string
    sidebar_collapsed: boolean
    show_breadcrumbs: boolean
  }
}

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('general')
  const [showPasswords, setShowPasswords] = useState(false)
  const [settings, setSettings] = useState<SettingsConfig>({
    general: {
      app_name: 'Admin Panel',
      app_url: 'https://admin.miapp.com',
      timezone: 'America/Lima',
      date_format: 'DD/MM/YYYY',
      language: 'es'
    },
    security: {
      password_min_length: 8,
      require_2fa: false,
      session_timeout: 1440, // minutos
      max_login_attempts: 5
    },
    database: {
      host: 'localhost',
      port: 3306,
      name: 'admin_panel',
      username: 'admin',
      password: '••••••••',
      ssl_enabled: false
    },
    email: {
      driver: 'smtp',
      smtp_host: 'smtp.gmail.com',
      smtp_port: 587,
      smtp_username: 'admin@miapp.com',
      smtp_password: '••••••••',
      from_address: 'noreply@miapp.com',
      from_name: 'Admin Panel'
    },
    api: {
      rate_limit: 100,
      cors_enabled: true,
      allowed_origins: ['http://localhost:3000', 'https://miapp.com'],
      api_version: 'v1'
    },
    ui: {
      theme: 'light',
      primary_color: '#3b82f6',
      sidebar_collapsed: false,
      show_breadcrumbs: true
    }
  })

  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    setIsSaving(true)
    
    // Simular guardado
    setTimeout(() => {
      toast.success('Configuración guardada exitosamente')
      setIsSaving(false)
    }, 1000)
  }

  const handleInputChange = (section: keyof SettingsConfig, field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }))
  }

  const tabs = [
    { id: 'general', label: 'General', icon: Globe },
    { id: 'security', label: 'Seguridad', icon: Shield },
    { id: 'database', label: 'Base de Datos', icon: Database },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'api', label: 'API', icon: Code },
    { id: 'ui', label: 'Interfaz', icon: Palette }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configuración del Sistema</h1>
          <p className="mt-2 text-sm text-gray-600">
            Administra la configuración global de la aplicación
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn btn-primary flex items-center"
        >
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Guardando...' : 'Guardar Cambios'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <nav className="space-y-2">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-primary-50 text-primary-700 border-primary-200'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {tab.label}
                </button>
              )
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* General Settings */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Configuración General</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre de la Aplicación
                      </label>
                      <input
                        type="text"
                        value={settings.general.app_name}
                        onChange={(e) => handleInputChange('general', 'app_name', e.target.value)}
                        className="input w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        URL de la Aplicación
                      </label>
                      <input
                        type="url"
                        value={settings.general.app_url}
                        onChange={(e) => handleInputChange('general', 'app_url', e.target.value)}
                        className="input w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Zona Horaria
                      </label>
                      <select
                        value={settings.general.timezone}
                        onChange={(e) => handleInputChange('general', 'timezone', e.target.value)}
                        className="input w-full"
                      >
                        <option value="America/Lima">Lima (GMT-5)</option>
                        <option value="America/New_York">New York (GMT-5)</option>
                        <option value="Europe/Madrid">Madrid (GMT+1)</option>
                        <option value="UTC">UTC (GMT+0)</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Formato de Fecha
                      </label>
                      <select
                        value={settings.general.date_format}
                        onChange={(e) => handleInputChange('general', 'date_format', e.target.value)}
                        className="input w-full"
                      >
                        <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                        <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                        <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Idioma
                      </label>
                      <select
                        value={settings.general.language}
                        onChange={(e) => handleInputChange('general', 'language', e.target.value)}
                        className="input w-full"
                      >
                        <option value="es">Español</option>
                        <option value="en">English</option>
                        <option value="pt">Português</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Configuración de Seguridad</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Longitud Mínima de Contraseña
                      </label>
                      <input
                        type="number"
                        min="6"
                        max="20"
                        value={settings.security.password_min_length}
                        onChange={(e) => handleInputChange('security', 'password_min_length', parseInt(e.target.value))}
                        className="input w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tiempo de Sesión (minutos)
                      </label>
                      <input
                        type="number"
                        value={settings.security.session_timeout}
                        onChange={(e) => handleInputChange('security', 'session_timeout', parseInt(e.target.value))}
                        className="input w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Máximo Intentos de Login
                      </label>
                      <input
                        type="number"
                        min="3"
                        max="10"
                        value={settings.security.max_login_attempts}
                        onChange={(e) => handleInputChange('security', 'max_login_attempts', parseInt(e.target.value))}
                        className="input w-full"
                      />
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="require_2fa"
                        checked={settings.security.require_2fa}
                        onChange={(e) => handleInputChange('security', 'require_2fa', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <label htmlFor="require_2fa" className="ml-2 text-sm text-gray-700">
                        Requerir Autenticación de Dos Factores
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Database Settings */}
            {activeTab === 'database' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Configuración de Base de Datos</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Ten cuidado al modificar esta configuración. Cambios incorrectos pueden afectar el funcionamiento del sistema.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Host
                      </label>
                      <input
                        type="text"
                        value={settings.database.host}
                        onChange={(e) => handleInputChange('database', 'host', e.target.value)}
                        className="input w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Puerto
                      </label>
                      <input
                        type="number"
                        value={settings.database.port}
                        onChange={(e) => handleInputChange('database', 'port', parseInt(e.target.value))}
                        className="input w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre de Base de Datos
                      </label>
                      <input
                        type="text"
                        value={settings.database.name}
                        onChange={(e) => handleInputChange('database', 'name', e.target.value)}
                        className="input w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Usuario
                      </label>
                      <input
                        type="text"
                        value={settings.database.username}
                        onChange={(e) => handleInputChange('database', 'username', e.target.value)}
                        className="input w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contraseña
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords ? "text" : "password"}
                          value={settings.database.password}
                          onChange={(e) => handleInputChange('database', 'password', e.target.value)}
                          className="input w-full pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(!showPasswords)}
                          className="absolute inset-y-0 right-0 flex items-center pr-3"
                        >
                          {showPasswords ? (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="ssl_enabled"
                        checked={settings.database.ssl_enabled}
                        onChange={(e) => handleInputChange('database', 'ssl_enabled', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <label htmlFor="ssl_enabled" className="ml-2 text-sm text-gray-700">
                        Habilitar SSL
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Email Settings */}
            {activeTab === 'email' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Configuración de Email</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Servidor SMTP
                      </label>
                      <input
                        type="text"
                        value={settings.email.smtp_host}
                        onChange={(e) => handleInputChange('email', 'smtp_host', e.target.value)}
                        className="input w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Puerto SMTP
                      </label>
                      <input
                        type="number"
                        value={settings.email.smtp_port}
                        onChange={(e) => handleInputChange('email', 'smtp_port', parseInt(e.target.value))}
                        className="input w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Usuario SMTP
                      </label>
                      <input
                        type="email"
                        value={settings.email.smtp_username}
                        onChange={(e) => handleInputChange('email', 'smtp_username', e.target.value)}
                        className="input w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contraseña SMTP
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords ? "text" : "password"}
                          value={settings.email.smtp_password}
                          onChange={(e) => handleInputChange('email', 'smtp_password', e.target.value)}
                          className="input w-full pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPasswords(!showPasswords)}
                          className="absolute inset-y-0 right-0 flex items-center pr-3"
                        >
                          {showPasswords ? (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          ) : (
                            <Eye className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Remitente
                      </label>
                      <input
                        type="email"
                        value={settings.email.from_address}
                        onChange={(e) => handleInputChange('email', 'from_address', e.target.value)}
                        className="input w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre Remitente
                      </label>
                      <input
                        type="text"
                        value={settings.email.from_name}
                        onChange={(e) => handleInputChange('email', 'from_name', e.target.value)}
                        className="input w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* API Settings */}
            {activeTab === 'api' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Configuración de API</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Límite de Peticiones por Minuto
                      </label>
                      <input
                        type="number"
                        value={settings.api.rate_limit}
                        onChange={(e) => handleInputChange('api', 'rate_limit', parseInt(e.target.value))}
                        className="input w-full"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Versión de API
                      </label>
                      <input
                        type="text"
                        value={settings.api.api_version}
                        onChange={(e) => handleInputChange('api', 'api_version', e.target.value)}
                        className="input w-full"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <div className="flex items-center mb-4">
                        <input
                          type="checkbox"
                          id="cors_enabled"
                          checked={settings.api.cors_enabled}
                          onChange={(e) => handleInputChange('api', 'cors_enabled', e.target.checked)}
                          className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                        />
                        <label htmlFor="cors_enabled" className="ml-2 text-sm text-gray-700">
                          Habilitar CORS
                        </label>
                      </div>
                      
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Orígenes Permitidos (uno por línea)
                      </label>
                      <textarea
                        rows={4}
                        value={settings.api.allowed_origins.join('\n')}
                        onChange={(e) => handleInputChange('api', 'allowed_origins', e.target.value.split('\n').filter(Boolean))}
                        className="input w-full"
                        placeholder="http://localhost:3000&#10;https://miapp.com"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* UI Settings */}
            {activeTab === 'ui' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Configuración de Interfaz</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tema
                      </label>
                      <select
                        value={settings.ui.theme}
                        onChange={(e) => handleInputChange('ui', 'theme', e.target.value)}
                        className="input w-full"
                      >
                        <option value="light">Claro</option>
                        <option value="dark">Oscuro</option>
                        <option value="auto">Automático</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Color Primario
                      </label>
                      <input
                        type="color"
                        value={settings.ui.primary_color}
                        onChange={(e) => handleInputChange('ui', 'primary_color', e.target.value)}
                        className="h-10 w-full rounded-md border border-gray-300"
                      />
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="sidebar_collapsed"
                        checked={settings.ui.sidebar_collapsed}
                        onChange={(e) => handleInputChange('ui', 'sidebar_collapsed', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <label htmlFor="sidebar_collapsed" className="ml-2 text-sm text-gray-700">
                        Sidebar Colapsado por Defecto
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="show_breadcrumbs"
                        checked={settings.ui.show_breadcrumbs}
                        onChange={(e) => handleInputChange('ui', 'show_breadcrumbs', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                      />
                      <label htmlFor="show_breadcrumbs" className="ml-2 text-sm text-gray-700">
                        Mostrar Breadcrumbs
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default SettingsPage
